import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { Users, LogIn, Eye, Activity, ArrowLeft, Shield } from 'lucide-react';
import { getUsageSummary } from '../api';
import { ALL_TABS, HOME_ONLY_TABS, PILLAR_BUBBLE_TABS } from '../tabs';

// Reuse the app's own tab definitions for page labels instead of maintaining a second id->label
// map here — falls back to a title-cased id for anything tracked that isn't in any tabs.js list
// (e.g. GPS sub-tabs, player-app pages).
const PAGE_LABELS = Object.fromEntries(
  [...ALL_TABS, ...HOME_ONLY_TABS, ...PILLAR_BUBBLE_TABS].map(t => [t.id, t.label])
);
PAGE_LABELS['usage-tracking'] = 'Usage Analytics';

function titleCase(id) {
  return id.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Player app routes (2026-08-24) — logged by PlayerApp.js as 'player/<path after /player>',
// e.g. 'player/me/gps/player-chronic'. Static routes get a friendly label below; the GPS
// sub-route reuses the same id-based lookup as the staff-side GPS tabs (pageLabel itself, via
// the fallback title-case) since player-side and staff-side GPS pages share tab ids.
const PLAYER_ROUTE_LABELS = {
  'today':          'Today',
  'forms':          'Forms',
  'forms/wellness': 'Wellness Form',
  'forms/rpe':      'RPE Form (Pitch)',
  'forms/rpe-gym':  'RPE Form (Gym)',
  'me':             'Player Hub',
  'me/gps':         'My GPS & Load',
  'me/wellness':    'My Wellness',
  'me/medical':     'My Medical',
  'me/tests':       'My Physical Tests',
  'me/ssg':         'My SSG',
  'me/technical':   'My Technical',
  'team':           'Team (player view)',
  'settings':       'Player Settings',
};

function pageLabel(id) {
  if (!id) return '—';
  if (id.startsWith('player/')) {
    const suffix = id.slice('player/'.length);
    if (PLAYER_ROUTE_LABELS[suffix]) return `Player App · ${PLAYER_ROUTE_LABELS[suffix]}`;
    if (suffix.startsWith('me/gps/')) return `Player App · My GPS & Load · ${pageLabel(suffix.slice('me/gps/'.length))}`;
    if (suffix.startsWith('forms/')) return `Player App · Forms`;
    return `Player App · ${titleCase(suffix.split('/').pop())}`;
  }
  return PAGE_LABELS[id] || titleCase(id);
}

function relativeTime(iso) {
  if (!iso) return 'Never';
  const diffMs = Date.now() - new Date(iso).getTime();
  const min = 60 * 1000, hr = 60 * min, day = 24 * hr;
  if (diffMs < min) return 'Just now';
  if (diffMs < hr) return `${Math.floor(diffMs / min)}m ago`;
  if (diffMs < day) return `${Math.floor(diffMs / hr)}h ago`;
  if (diffMs < 30 * day) return `${Math.floor(diffMs / day)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px 22px' };

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div style={{ ...card, flex: '1 1 180px', minWidth: 160 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
        <Icon size={14} color={accent} /> {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ color: 'var(--text-secondary)' }}>
          {p.name}: <strong style={{ color: 'var(--text-primary)' }}>{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

export default function UsageTracking({ user, onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('lastActive');
  const [excludeAdminViews, setExcludeAdminViews] = useState(false);
  const [showAllPages, setShowAllPages] = useState(false);

  useEffect(() => {
    getUsageSummary(user.id)
      .then(setData)
      .catch(err => setError(err.message || 'Failed to load usage data.'));
  }, [user.id]);

  // Both variants (all views / admin-excluded) come from the backend in one response, so the
  // toggle switches instantly with no refetch. Capped at 15 (backend sends up to 25) so "show
  // all" still stays readable — top 8 shown by default, expandable from there.
  const allPagesRanked = useMemo(() => {
    if (!data) return [];
    const source = excludeAdminViews ? (data.topPagesNoAdmin || []) : (data.topPages || []);
    return source.slice(0, 15).map(p => ({ ...p, label: pageLabel(p.page) }));
  }, [data, excludeAdminViews]);
  const pagesToShow = showAllPages ? allPagesRanked : allPagesRanked.slice(0, 8);

  const sortedUsers = useMemo(() => {
    if (!data) return [];
    const rows = [...data.byUser];
    const cmp = {
      lastActive: (a, b) => (b.lastActive || '').localeCompare(a.lastActive || ''),
      name:       (a, b) => (a.name || '').localeCompare(b.name || ''),
      logins:     (a, b) => b.loginCount - a.loginCount,
      pageViews:  (a, b) => b.pageViewCount - a.pageViewCount,
    }[sortBy];
    return rows.sort(cmp);
  }, [data, sortBy]);

  const totals = useMemo(() => {
    if (!data) return null;
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return {
      accounts: data.byUser.length,
      activeToday: data.byUser.filter(u => u.lastActive && u.lastActive.slice(0, 10) === today).length,
      activeThisWeek: data.byUser.filter(u => u.lastActive && new Date(u.lastActive).getTime() >= weekAgo).length,
      totalLogins: data.byUser.reduce((sum, u) => sum + u.loginCount, 0),
      totalPageViews: data.byUser.reduce((sum, u) => sum + u.pageViewCount, 0),
    };
  }, [data]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Usage Analytics</h1>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, background: 'var(--border)', color: 'var(--text-muted)', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>
              <Shield size={11} /> Admin only
            </span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '4px 0 0' }}>
            Who's using HeraLabX, how often, and which pages they visit.
          </p>
        </div>
        {onNavigate && (
          <button
            onClick={() => onNavigate('settings')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
          >
            <ArrowLeft size={14} /> Back to Settings
          </button>
        )}
      </div>

      {error && (
        <div style={{ ...card, borderColor: '#f87171', color: '#f87171', marginBottom: 20 }}>{error}</div>
      )}

      {!data && !error && (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 48 }}>Loading usage data…</div>
      )}

      {data && totals && (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
            <StatCard icon={Users} label="Accounts" value={totals.accounts} accent="#3b82f6" />
            <StatCard icon={Activity} label="Active Today" value={totals.activeToday} accent="#22c55e" />
            <StatCard icon={Activity} label="Active This Week" value={totals.activeThisWeek} accent="#22c55e" />
            <StatCard icon={LogIn} label="Total Logins" value={totals.totalLogins} accent="#f59e0b" />
            <StatCard icon={Eye} label="Page Views (30d)" value={totals.totalPageViews} accent="#a855f7" />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ ...card, flex: '2 1 480px', minWidth: 320 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14 }}>Daily Active Users (last 30 days)</div>
              {data.daily.length === 0 ? (
                <div style={{ color: 'var(--text-dimmed)', fontSize: 12, padding: '24px 0', textAlign: 'center' }}>No activity recorded yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={data.daily}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: 'var(--text-dimmed)' }} tickFormatter={d => d.slice(5)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-dimmed)' }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Line type="monotone" dataKey="activeUsers" name="Active users" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>

            <div style={{ ...card, flex: '1 1 320px', minWidth: 280 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Most Visited Pages (30d)</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={excludeAdminViews} onChange={e => setExcludeAdminViews(e.target.checked)} />
                  Exclude admin views
                </label>
              </div>
              {pagesToShow.length === 0 ? (
                <div style={{ color: 'var(--text-dimmed)', fontSize: 12, padding: '24px 0', textAlign: 'center' }}>
                  {excludeAdminViews ? 'No non-admin page views recorded yet.' : 'No page views recorded yet.'}
                </div>
              ) : (
                <div style={{ maxHeight: 320, overflowY: pagesToShow.length > 10 ? 'auto' : 'visible' }}>
                  <ResponsiveContainer width="100%" height={Math.max(220, pagesToShow.length * 26)}>
                    <BarChart data={pagesToShow} layout="vertical" margin={{ left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-dimmed)' }} />
                      <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 10, fill: 'var(--text-dimmed)' }}
                        tickFormatter={v => v.length > 24 ? `${v.slice(0, 23)}…` : v} />
                      <Tooltip content={<ChartTooltip />} />
                      <Bar dataKey="views" name="Views" fill="#a855f7" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {allPagesRanked.length > 8 && (
                <button
                  onClick={() => setShowAllPages(s => !s)}
                  style={{ marginTop: 10, padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}
                >
                  {showAllPages ? 'Show top 8' : `Show all ${allPagesRanked.length} pages`}
                </button>
              )}
            </div>
          </div>

          <div style={{ ...card, padding: 0, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Per Account</div>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{ fontSize: 12, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)' }}
              >
                <option value="lastActive">Sort by last active</option>
                <option value="name">Sort by name</option>
                <option value="logins">Sort by logins</option>
                <option value="pageViews">Sort by page views</option>
              </select>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--text-dimmed)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <th style={{ padding: '10px 20px' }}>Account</th>
                    <th style={{ padding: '10px 20px' }}>Role</th>
                    <th style={{ padding: '10px 20px' }}>Logins</th>
                    <th style={{ padding: '10px 20px' }}>Page Views</th>
                    <th style={{ padding: '10px 20px' }}>Last Login</th>
                    <th style={{ padding: '10px 20px' }}>Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map(u => (
                    <tr key={u.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 20px' }}>
                        <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>@{u.username}</div>
                      </td>
                      <td style={{ padding: '10px 20px', color: 'var(--text-muted)' }}>
                        {u.isAdmin ? 'Admin' : u.role}
                      </td>
                      <td style={{ padding: '10px 20px', color: 'var(--text-secondary)' }}>{u.loginCount}</td>
                      <td style={{ padding: '10px 20px', color: 'var(--text-secondary)' }}>{u.pageViewCount}</td>
                      <td style={{ padding: '10px 20px', color: 'var(--text-muted)' }}>{relativeTime(u.lastLogin)}</td>
                      <td style={{ padding: '10px 20px', color: 'var(--text-muted)' }}>{relativeTime(u.lastActive)}</td>
                    </tr>
                  ))}
                  {sortedUsers.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 32 }}>No accounts found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              Recent Activity
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto' }}>
              {data.recent.length === 0 ? (
                <div style={{ color: 'var(--text-dimmed)', fontSize: 12, padding: 32, textAlign: 'center' }}>No activity recorded yet.</div>
              ) : data.recent.map(e => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', borderTop: '1px solid var(--border)', fontSize: 12 }}>
                  {e.event_type === 'login'
                    ? <LogIn size={13} color="#f59e0b" />
                    : <Eye size={13} color="#a855f7" />}
                  <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{e.name || e.username || 'Unknown'}</span>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {e.event_type === 'login' ? 'logged in' : `viewed ${pageLabel(e.page)}`}
                  </span>
                  <span style={{ marginLeft: 'auto', color: 'var(--text-dimmed)', whiteSpace: 'nowrap' }}>{relativeTime(e.created_at)}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
